import { memo, useRef } from "react";
import Grid from "@mui/material/Grid";
import {
  maxLength,
  minLength,
  minValue,
  number,
  required,
  TextInput,
  useDataProvider,
} from "react-admin";
import MuiDatePickerInput from "../../../common/MuiDatePickerInput";

import { toCurrency } from "../../../../utils/formatter";
import InputAdornment from "@mui/material/InputAdornment";
import { useFormContext } from "react-hook-form";
import parserFormatNumInput from "../../../../utils/parserFormatNumInput";
import PurchaseOrderReference from "../../common/PurchaseOrderReference";
import useEnterToAddRowAndFocus from "../../../../utils/useEnterToAddRowAndFocus"; // ✅ import

const equalOrLessThanDebt = (value, allValues, elm) => {
  const splite = elm.source.split(".");
  const debt = allValues[splite[0]][+splite[1]].debt;
  if (+value > +debt) {
    return (
      "Must be equal or less than " + toCurrency(false).format(debt) + " (debt)"
    );
  }
  return undefined;
};
const equalOrLessThanTotal = (value, allValues, elm) => {
  let remain = allValues.total;
  let count = 0;
  const splite = elm.source.split(".");
  let skipProcessLast = 0;
  for (let i = 0; i < allValues.factory_debt_payment_items.length; i++) {
    const elm = allValues.factory_debt_payment_items[i];
    if (+splite[1] === i) {
      skipProcessLast = elm.amount;
      continue;
    }
    count += +elm.amount;
    if (+count > +allValues.total) {
      return (
        "Must be equal or less than " +
        toCurrency(false).format(remain) +
        " (remaining of total payment)"
      );
    }
    remain -= +elm.amount;
  }
  count += +skipProcessLast;
  if (+count > +allValues.total) {
    return (
      "Must be equal or less than " +
      toCurrency(false).format(remain) +
      " (remaining of total payment)"
    );
  }
  return undefined;
};

const uniqueItem = (value, allValues, elm) => {
  let count = 0;
  for (let i = 0; i < allValues.factory_debt_payment_items.length; i++) {
    const elm = allValues.factory_debt_payment_items[i];
    if (value === elm.purchase_order_id) {
      if (count > 0) {
        return "Duplicate is not allowed";
      }
      count += 1;
    }
  }
  return undefined;
};

const validateCode = [required(), minLength(36), maxLength(36), uniqueItem];
const validateAmount = [
  required(),
  number(),
  minValue(1),
  equalOrLessThanDebt,
  equalOrLessThanTotal,
];

const CstInput = ({
  formData,
  purchaseOrderIdSource,
  dueDateSource,
  debtSource,
  transactionDateSource,
  amountSource,
  addRow, // ✅ perlu diteruskan dari ArrayInput
}) => {
  const { setValue } = useFormContext();
  const dataProvider = useDataProvider();

  const amountRef = useRef(null); // ✅ ref untuk payment

  // 🔹 Enter di payment → tambah baris baru & fokus ke kode PO
  useEnterToAddRowAndFocus(amountRef, addRow, 'input[name*="purchase_order_id"]');

  const onCodeChangeHandler = async (id) => {
    if (id.length !== 36) return;
    try {
      const response = await dataProvider.getListNoParams(
        "purchase-orders/" + encodeURIComponent(id)
      );

      if (response && response.company_debt) {
        setValue(transactionDateSource, response.created_at);
        setValue(dueDateSource, response.company_debt.due_date);
        setValue(debtSource, response.company_debt.remain);
      }
    } catch (error) {
      console.error("Error fetching purchase order:", error);
    }
  };

  return (
    <Grid container spacing={2} sx={{ "& .MuiGrid-item": { py: 0 }, mt: 0 }}>
      <Grid item xs={12} lg={6.4}>
        <PurchaseOrderReference
          source={purchaseOrderIdSource}
          validate={validateCode}
          dependOn="supplier_id"
          queryName="supplier_id"
          resource="purchase_orders"
          reference="purchase-orders"
          defaultFilters={{ "CompanyDebt.remain": ">0" }}
          formData={formData}
          onChange={onCodeChangeHandler}
        />
      </Grid>
      <Grid item xs={12} lg={1.4}>
        <MuiDatePickerInput
          variant="outlined"
          source={transactionDateSource}
          label="mine.label.transaction_date"
          disabled
          fullWidth
        />
      </Grid>
      <Grid item xs={12} lg={1.4}>
        <MuiDatePickerInput
          variant="outlined"
          source={dueDateSource}
          label="mine.label.due_date"
          disabled
          fullWidth
        />
      </Grid>
      <Grid item xs={12} lg={1.4}>
        <TextInput
          variant="outlined"
          source={debtSource}
          label="mine.label.debt"
          fullWidth
          disabled
          InputProps={{ startAdornment: <InputAdornment position="start">Rp</InputAdornment> }}
          {...parserFormatNumInput}
        />
      </Grid>
      <Grid item xs={12} lg={1.4} ref={amountRef}>
        <TextInput
          variant="outlined"
          source={amountSource}
          label="mine.label.payment"
          fullWidth
          validate={validateAmount}
          InputProps={{ startAdornment: <InputAdornment position="start">Rp</InputAdornment> }}
          {...parserFormatNumInput}
        />
      </Grid>
    </Grid>
  );
};

const propsAreEqual = (prevProps, nextProps) => {
  return prevProps.amount === nextProps.amount && prevProps.purchase_order_id === nextProps.purchase_order_id;
};

const Beruk = memo(CstInput, propsAreEqual);

const Inputs = ({ formData, scopedFormData, getSource, addRow }) => {
  return (
    <Beruk
      {...scopedFormData}
      formData={formData}
      purchaseOrderIdSource={getSource("purchase_order_id")}
      transactionDateSource={getSource("transaction_date")}
      dueDateSource={getSource("due_date")}
      debtSource={getSource("debt")}
      amountSource={getSource("amount")}
      addRow={addRow} // ✅ diteruskan ke komponen
    />
  );
};

export default Inputs;
